import { Box } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { FetchedStockInterface } from 'lib/hooks/useStockStore'
import { getGameCodeFromName } from 'lib/utils'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Game,
  gameMetaData,
  GAME_NAMES,
  getGameFromId,
  InventoryArticle,
  isSingle,
  ONBOARDING_STEPS,
} from 'shared'
import { ImportFileCsv } from '../csv/ImportFileCsv'
import { CompleteDataInGradedCards } from '../inventory/CompleteDataInGradedCards'
import { checkIfThereAreGradedCards } from './onboarding-utils'
import { checkIfFetchedStockCommentContainsTaxationKeywords } from './OnboardingPresenter'

export interface ImportCsvStockProps {
  setStep: (value: number) => void
  setVisitedSteps: React.Dispatch<React.SetStateAction<number[]>>
  changeFlowWithFixCommentsStep: boolean
  setInventoryState: React.Dispatch<React.SetStateAction<FetchedStockInterface | undefined>>
  setUserOnboarded: (updatedInventory: InventoryArticle[]) => Promise<void>
}

export const ImportCsvStock = ({
  setStep,
  setVisitedSteps,
  setInventoryState,
  changeFlowWithFixCommentsStep,
  setUserOnboarded,
}: ImportCsvStockProps): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [workingFetchedStock, setWorkingFetchedStock] = useState<InventoryArticle[]>([])

  const [thereAreGradedCards, setThereAreGradedCards] = useState(false)
  const [importFileDialogOpen, setImportFileDialogOpen] = useState(false)

  const [importedCsv, setImportedCsv] = useState<{
    [key: number]: InventoryArticle[]
  }>({})
  const [importedGame, setImportedGame] = useState<{
    [key: number]: boolean
  }>({})
  const [selectedGame, setSelectedGame] = useState<Game | undefined>(undefined)

  const openImportDialog = () => {
    setImportFileDialogOpen(true)
  }

  const handleReadCsv = async (importedArticles: InventoryArticle[]) => {
    if (!selectedGame) {
      enqueueSnackbar('error', { variant: 'error' })
      return
    }

    if (importedArticles.length > 0) {
      const newInventory = importedArticles.map((article) => {
        const locations = [
          {
            _id: '',
            userId: '',
            ptArticleId: 0,
            name: article.location ?? '',
            quantity: article.quantity,
            deltaQuantity: article.quantity,
            totalPickedQuantity: 0,
            totalQuantityWithIssues: 0,
          },
        ]
        article.locations = locations
        return new InventoryArticle(article)
      })

      const copyOfImportedCsv = { ...importedCsv }

      copyOfImportedCsv[selectedGame.idGame] = newInventory

      setImportedCsv(copyOfImportedCsv)
      const copyOfImportedGame = { ...importedGame }
      copyOfImportedGame[selectedGame.idGame] = true
      setImportedGame(copyOfImportedGame)
    } else {
      setImportFileDialogOpen(false)
      enqueueSnackbar(`${t('onboarding.errorImport')}`, { variant: 'error' })
    }
  }

  /**
   * after having uploaded all csv upload entire stock
   */
  const uploadStock = () => {
    let finalInventory: InventoryArticle[] = []
    Object.keys(importedCsv).map((idGame) => {
      const stock = importedCsv[Number(parseInt(idGame))]
      finalInventory = finalInventory.concat(stock)
    })

    setWorkingFetchedStock(finalInventory)
  }

  useEffect(() => {
    if (workingFetchedStock.length > 0) {
      const thereAreGradedCards = checkIfThereAreGradedCards(workingFetchedStock)
      if (thereAreGradedCards) setThereAreGradedCards(thereAreGradedCards)
      else checkDifferentCommentsOrTaxationComments()
    }
  }, [workingFetchedStock])

  const checkDifferentCommentsOrTaxationComments = () => {
    const stockData: InventoryArticle[] = []
    const sealedStockData: InventoryArticle[] = []
    workingFetchedStock.forEach((article) => {
      isSingle(article.card) ? stockData.push(article) : sealedStockData.push(article)
    })
    const newState = {
      stock: stockData,
      sealedStock: sealedStockData,
      fetchedAt: new Date().toISOString(),
    }
    setInventoryState(newState)
    let containsKeywords = false

    /*
    if changeFlowWithFixCommentsStep is true, then I have already gone through the first flow where I uploaded/fetched the stock and particular comments emerged.
    so the user is currently uploading his correct stock and does not want to go through the comments form step again
    */
    if (!changeFlowWithFixCommentsStep) {
      containsKeywords = checkIfFetchedStockCommentContainsTaxationKeywords(
        stockData.concat(sealedStockData)
      )
      if (containsKeywords) {
        setVisitedSteps((prev) => [...prev, ONBOARDING_STEPS.COMMENTS_FORM.idx])
        setStep(ONBOARDING_STEPS.COMMENTS_FORM.idx)
      } else {
        setVisitedSteps((prev) => [...prev, ONBOARDING_STEPS.SET_PRICES.idx])
        setStep(ONBOARDING_STEPS.SET_PRICES.idx)
      }
    } else {
      setUserOnboarded(workingFetchedStock)
    }
  }

  const removeUploadedStock = (game: Game) => {
    const copyOfImportedCsv = importedCsv
    delete copyOfImportedCsv[game.idGame]

    const copyOfImportedGame = { ...importedGame }
    copyOfImportedGame[game.idGame] = false
    setImportedGame(copyOfImportedGame)

    setImportedCsv(copyOfImportedCsv)
  }

  const handleConfirmGradedCards = (newIncomingStock: InventoryArticle[]) => {
    setWorkingFetchedStock(newIncomingStock)
    setThereAreGradedCards(false)
  }

  useEffect(() => {
    if (workingFetchedStock.length !== 0 && !thereAreGradedCards) {
      checkDifferentCommentsOrTaxationComments()
    }
  }, [thereAreGradedCards])

  const getImportCsvView = () => {
    return (
      <div className="d-flex justify-content-center w-100 onboarding-container">
        <div className="wizard-content mt-lg-5 container">
          <div className="row justify-content-between">
            <div className="col-lg-6 mb-lg-0 mb-4">
              <h3 className="fw-normal">{t('onboarding.uploadCsv')}</h3>
              <p className="fs-6 fw-light">{t('onboarding.importCsv')}</p>
            </div>
            <div className="col-lg-6">
              <div className="w-100 d-flex flex-wrap justify-content-center">
                {gameMetaData.slice(1).map((game) => {
                  return (
                    <div key={game.idGame} className=" mx-2 mt-2">
                      {importedGame[game.idGame] === true ? (
                        <div
                          className="d-flex flex-row import-csv-game-btn"
                          style={{
                            border: '1px solid #013685',
                            borderRadius: '0.45rem',
                            backgroundColor: '#013685',
                          }}
                        >
                          <Button
                            data-testid={`remove-imported-${game.idGame}`}
                            onClick={() => removeUploadedStock(game)}
                            style={{ width: '20px', height: '100%' }}
                            variant="contained"
                            tooltip={`Remove ${game.name}`}
                          >
                            <i className="fa-regular fa-xmark"></i>
                          </Button>
                          <Button variant="contained" style={{ padding: '6px', width: '100%' }}>
                            {' '}
                            Imported {GAME_NAMES[game.idGame]}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          data-testid={`import-${game.idGame}`}
                          variant="outlined"
                          className="import-csv-game-btn"
                          onClick={() => {
                            setSelectedGame(game)
                            openImportDialog()
                          }}
                        >
                          <div className="w-100 d-flex flex-row align-items-center">
                            <Box width={60} display={'flex'} justifyContent={'center'}>
                              <span
                                className={`ss-${getGameCodeFromName(
                                  getGameFromId(game.idGame)
                                )} ss-game-icon ss-fw`}
                                style={{ fontSize: '1.5rem' }}
                              ></span>
                            </Box>
                            <span>Import {GAME_NAMES[game.idGame]}</span>
                          </div>
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className="w-100 d-flex justify-content-center mt-5">
                {changeFlowWithFixCommentsStep ? (
                  <Button
                    data-testid="upload-csv"
                    variant="contained"
                    onClick={uploadStock}
                    style={{ width: '232px' }}
                  >
                    Carica e concludi
                  </Button>
                ) : (
                  <Button
                    data-testid="upload-csv"
                    variant="contained"
                    onClick={uploadStock}
                    style={{ width: '232px' }}
                  >
                    Upload
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {!thereAreGradedCards && getImportCsvView()}

      {thereAreGradedCards && (
        <div className="wizard-content container">
          <CompleteDataInGradedCards
            incomingStock={workingFetchedStock}
            handleConfirmGradedCards={handleConfirmGradedCards}
          />
        </div>
      )}

      {importFileDialogOpen && (
        <ClosableDialog
          maxWidth="xl"
          fullWidth={true}
          onClose={() => {
            setImportFileDialogOpen(false)
          }}
          open={importFileDialogOpen}
        >
          <Box minWidth={{ xs: 0, md: '780px' }} margin={{ xs: 1, sm: 3 }}>
            <ImportFileCsv
              handleResult={handleReadCsv}
              closeExternalDialog={setImportFileDialogOpen}
              importedCsvGame={selectedGame}
            />
          </Box>
        </ClosableDialog>
      )}
    </>
  )
}
